import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BillingCtx = createContext(null);
export const PLAN_RANK = { free: 0, pro: 1, premium: 2 };

// Stripe Checkout/Portal refuse to render inside third-party iframes (blank page). When EV-IDEX is embedded
// (e.g. the Emergent preview), open Stripe in a new top-level tab instead of navigating the frame.
export const goToStripe = (url) => {
  if (window.top === window.self) { window.location.assign(url); return; }
  window.open(url, "_blank", "noopener,noreferrer");
  toast.message("Stripe opened in a new tab", {
    description: "Complete the secure Stripe step there — this page refreshes automatically when you come back.",
    action: { label: "Open again", onClick: () => window.open(url, "_blank", "noopener,noreferrer") }, duration: 15000,
  });
};

// Entitlements come from the server (/billing/me). UI gating here is cosmetic; the API enforces every paid route.
export const BillingProvider = ({ children }) => {
  const { user } = useAuth();
  const [billing, setBilling] = useState(null);
  const [mode, setMode] = useState(null);
  useEffect(() => { api.get("/billing/plans").then((r) => setMode(r.data.mode)).catch(() => {}); }, []);

  const refresh = useCallback(async (opts = {}) => {
    if (!user) { setBilling(null); return null; }
    try {
      const r = await api.get("/billing/me", { params: opts.sync ? { refresh: true } : {} });
      setBilling(r.data);
      return r.data;
    } catch (e) { console.warn("[apex] billing:", e?.message || e); return null; }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const h = () => refresh();
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("apx:billing-changed", h);
    window.addEventListener("focus", h);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("apx:billing-changed", h);
      window.removeEventListener("focus", h);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  const plan = billing?.plan || "free";
  const can = (feature) => !!billing?.entitlements?.[feature];

  const checkout = async (lookupKey) => {
    try {
      const r = await api.post("/payments/checkout", { lookup_key: lookupKey, origin_url: window.location.origin });
      if (r.data.checkout_url) { goToStripe(r.data.checkout_url); return; }
      if (r.data.changed) { toast.success(`Plan changed to ${r.data.plan === "premium" ? "Premium" : "Pro"}`); await refresh(); }
    } catch (e) { toast.error(apiError(e, "Could not start checkout")); }
  };

  const portal = async () => {
    try {
      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
      goToStripe(r.data.url);
    } catch (e) { toast.error(apiError(e, "Could not open billing portal")); }
  };

  return <BillingCtx.Provider value={{ billing, plan, can, refresh, checkout, portal, mode, loading: !!user && billing === null }}>{children}</BillingCtx.Provider>;
};

export const useBilling = () => useContext(BillingCtx);
